import { posthog } from 'posthog-js';
import { PostHogProvider as PostHogProviderFromPosthog } from 'posthog-js/react';
import type React from 'react';
import type { PropsWithChildren } from 'react';
import { useEffect, useRef } from 'react';
import { POSTHOG_HOST, POSTHOG_KEY } from '../../lib/constants.ts';
import { maskTapiPhoneUrl } from '../../lib/mask-tapi-phone-url.ts';

export const PosthogProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const init = useRef(false);

    useEffect(() => {
        if (!POSTHOG_KEY) {
            return;
        }
        if (typeof window === 'undefined') {
            return;
        }
        if (process.env.NODE_ENV === 'development') {
            return;
        }
        if (init.current) {
            return;
        }
        init.current = true;

        posthog.init(POSTHOG_KEY, {
            api_host: POSTHOG_HOST || 'https://app.posthog.com',
            loaded: (posthog) => {
                if (process.env.NODE_ENV === 'development') {
                    posthog.debug();
                }
            },
            // Strip the caller's phone number out of URL properties before any
            // event leaves the browser — the TAPI route carries it in the path.
            sanitize_properties: (properties) => {
                const urlKeys = [
                    '$current_url',
                    '$pathname',
                    '$referrer',
                    '$initial_current_url',
                    '$initial_pathname',
                    '$initial_referrer',
                ] as const;
                for (const key of urlKeys) {
                    const value: unknown = properties[key];
                    if (typeof value === 'string') {
                        properties[key] = maskTapiPhoneUrl(value);
                    }
                }
                return properties;
            },
            session_recording: {
                maskAllInputs: false,
                maskInputOptions: {
                    password: true,
                },
            },
        });

        posthog.register({
            touch:
                'ontouchstart' in window ||
                navigator.maxTouchPoints > 0 ||
                (navigator as unknown as { msMaxTouchPoints: number }).msMaxTouchPoints > 0,
        });

        posthog.capture('$set', {
            $set_once: { first_seen_date: new Date().toISOString() },
        });
    }, []);

    return <PostHogProviderFromPosthog client={posthog}>{children}</PostHogProviderFromPosthog>;
};
